import classNames from "classnames";
import { Field, Form, Formik } from "formik";
import { DateTime } from "luxon";
import { MouseEventHandler, useMemo } from "react";
import { useDispatch } from "react-redux";
import ReactTooltip from "react-tooltip";

import {
  CaseAdmin,
  CaseSpecimenAdmin,
  SpecimenAdmin,
  SpecimenProtocolOverride,
} from "pwapi";

import { updateSpecimen } from "shared/actions/admin/adminSpecimenActions";
import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import {
  HTML_DATETIME_LOCAL_INPUT_FORMAT,
  prepareDatetimeStringForAPI,
} from "shared/utils/dates";
import {
  getProtocolStatuses,
  procedureEnumToLabel,
  protocolLabels,
} from "shared/utils/specimenUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import CustomInput from "components/customInputs/CustomInput";
import SelectInput from "components/customInputs/SelectInput";
import TextareaInput from "components/customInputs/TextareaInput";
import TextInput from "components/customInputs/TextInput";
import { useAppSelector } from "store/hooks";

type SpecimenFormProps = {
  specimen: SpecimenAdmin | CaseSpecimenAdmin;
  adminCase: CaseAdminDetail;
  onClose: MouseEventHandler<HTMLButtonElement>;
  canDeleteSpecimen: boolean;
  setDelSpecimen: Function;
  disableSubmit?: boolean;
};

const setInitialValues = (
  initialValues: CaseSpecimenAdmin,
  initialSpecimen?: SpecimenAdmin | CaseSpecimenAdmin,
  adminCase?: CaseAdminDetail
) => {
  const values = { ...initialValues, ...initialSpecimen };

  if (values.collected_date) {
    values.collected_date = DateTime.fromISO(values.collected_date, {
      zone: adminCase?.collected_date_timezone,
    }).toFormat(HTML_DATETIME_LOCAL_INPUT_FORMAT);
  }

  if (values.received_date) {
    values.received_date = DateTime.fromISO(values.received_date, {
      zone: adminCase?.received_date_timezone,
    }).toFormat(HTML_DATETIME_LOCAL_INPUT_FORMAT);
  }

  return values;
};

const checkValuesDiff = (
  values: CaseSpecimenAdmin,
  initialValues: CaseSpecimenAdmin
) => {
  return Object.keys(initialValues).reduce(
    (acc: Partial<CaseSpecimenAdmin>, key) => {
      return values[key as keyof CaseSpecimenAdmin] &&
        values[key as keyof CaseSpecimenAdmin] !==
          initialValues[key as keyof CaseSpecimenAdmin]
        ? { ...acc, [key]: values[key as keyof CaseSpecimenAdmin] }
        : acc;
    },
    {}
  );
};

const SpecimenFormProtocols = ({
  specimen,
  setFieldValue,
}: {
  specimen: SpecimenAdmin | CaseSpecimenAdmin;
  setFieldValue: Function;
}) => {
  const protocolOverrides =
    (specimen as SpecimenAdmin).protocol_overrides || [];
  const protocolStatuses = getProtocolStatuses(specimen, true);

  const setProtocolOverride = (
    p: SpecimenProtocolOverride.protocol,
    v: string
  ) => {
    const newProtocolOverrides = protocolOverrides.filter(
      (po) => po.protocol !== p
    );

    if (v !== "null") {
      newProtocolOverrides.push({ protocol: p, active: v === "true" });
    }

    setFieldValue("protocol_overrides", newProtocolOverrides);
  };

  const protocolsStr = Object.entries(getProtocolStatuses(specimen))
    .filter(([_, status]) => status.active)
    .map(([protocol, _]) => protocol)
    .join(",");
  const initialProtocolsStr = useMemo(() => protocolsStr, []);

  const protocolsToShow = ["alopecia", "nail" /* "whole_specimen" */];

  const difEnabled = useAppSelector(
    (state) => !!state.sys.features.switches?.difProtocol
  );
  if (difEnabled) protocolsToShow.push("dif");

  return (
    <fieldset className="panelContainer__section">
      <legend className="panelContainer__section--title">Protocols</legend>
      {protocolsToShow.map((p) => (
        <div style={{ display: "flex" }} key={p}>
          <div
            className={classNames("Tag", {
              "Tag--blue":
                (protocolOverrides || []).find((po) => po.protocol === p) ===
                  undefined &&
                protocolStatuses[p as SpecimenProtocolOverride.protocol].active,
              "Tag--gray":
                (protocolOverrides || []).find((po) => po.protocol === p) ===
                  undefined &&
                !protocolStatuses[p as SpecimenProtocolOverride.protocol]
                  .active,
              "Tag--blue--bordered":
                (protocolOverrides || []).find((po) => po.protocol === p)
                  ?.active === true,
              "Tag--gray--bordered":
                (protocolOverrides || []).find((po) => po.protocol === p)
                  ?.active === false,
            })}
            data-tip={
              protocolStatuses[p as SpecimenProtocolOverride.protocol].reason
            }
            data-for="specimen-form-protocols"
          >
            {protocolLabels[p as SpecimenProtocolOverride.protocol]}
          </div>
          <div style={{ marginLeft: "auto" }}>
            <select
              onChange={(ev) =>
                setProtocolOverride(
                  p as SpecimenProtocolOverride.protocol,
                  ev.currentTarget.value
                )
              }
              value={
                protocolOverrides.find((po) => po.protocol === p) === undefined
                  ? "null"
                  : protocolOverrides.find((po) => po.protocol === p)?.active
                  ? "true"
                  : "false"
              }
            >
              <option value="null">
                Auto:{" "}
                {protocolStatuses[p as SpecimenProtocolOverride.protocol].active
                  ? "ON"
                  : "OFF"}
              </option>
              <option value="true">Override: ON</option>
              <option value="false">Override: OFF</option>
            </select>
          </div>
        </div>
      ))}
      {protocolsStr !== initialProtocolsStr && (
        <div style={{ fontStyle: "italic", color: "darkred" }}>
          Changing Protocols after Accessioning is not fully supported.
        </div>
      )}
      <ReactTooltip id="specimen-form-protocols" place="right" />
    </fieldset>
  );
};

const SpecimenForm = ({
  specimen,
  adminCase,
  onClose,
  setDelSpecimen,
  canDeleteSpecimen,
  disableSubmit = false,
}: SpecimenFormProps) => {
  const dispatch = useDispatch();

  const initialValues = setInitialValues(
    {
      name: "",
      collected_date: "",
      received_date: "",
      type: undefined,
      site: "",
      procedure: undefined,
      source: "",
      clinical_history: "",
      gross_description: "",
      protocol_overrides: [],
    },
    specimen,
    adminCase
  );

  const handleOnSubmit = (values: CaseSpecimenAdmin) => {
    if (disableSubmit) return;

    const updatedValues = checkValuesDiff(values, initialValues);
    //TODO: Swap out the submit form rehashing to just be the updatedValues.
    // We cannot do this quite yet as submit form is where the alternations based on WPL and PW are.
    const submitForm = {
      id: specimen?.id,
      clinical_history: updatedValues.clinical_history,
      gross_description: updatedValues.gross_description,
      name: updatedValues.name,
      protocol_overrides: updatedValues.protocol_overrides,

      ...(adminCase.source === CaseAdmin.source.PW
        ? {
            collected_date:
              values.collected_date &&
              prepareDatetimeStringForAPI(
                values.collected_date,
                adminCase.collected_date_timezone
              ),
            received_date:
              values.received_date &&
              prepareDatetimeStringForAPI(
                values.received_date,
                adminCase.received_date_timezone
              ),
            type: updatedValues.type,
            site: updatedValues.site,
            procedure: updatedValues.procedure,
          }
        : {
            source: updatedValues.source,
          }),
    };

    dispatch(
      updateSpecimen({
        specimen: submitForm,
        case_id: adminCase.id,
        onClose: onClose,
      })
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values) => handleOnSubmit(values)}
    >
      {({ resetForm, dirty, values, setFieldValue }) => (
        <Form className="AdminPanelForm">
          <div className="panelContainer">
            <fieldset className="panelContainer__section">
              <legend className="panelContainer__section--title">
                Specimen Details
              </legend>
              <Field name="name">
                {({ field }: any) => (
                  <TextInput
                    label="Name"
                    id="name"
                    {...field}
                    disabled={disableSubmit}
                  />
                )}
              </Field>
              {adminCase.source === CaseAdmin.source.PW && (
                <>
                  <div className="panelContainer__inputGroup">
                    <Field name="collected_date">
                      {({ field, form }: any) => (
                        <CustomInput label="Collected Date">
                          <input
                            type="datetime-local"
                            label="Collected Date"
                            id="collected_date"
                            {...field}
                            onChange={(e: any) => {
                              form.setFieldValue(
                                "collected_date",
                                e.target.value
                              );
                            }}
                            disabled={disableSubmit}
                          />
                        </CustomInput>
                      )}
                    </Field>
                    <CustomInput label="Collected Date Timezone">
                      {adminCase?.collected_date_timezone || "UTC"}
                    </CustomInput>
                  </div>
                  <div className="panelContainer__inputGroup">
                    <Field name="received_date">
                      {({ field, form }: any) => (
                        <CustomInput label="Received Date">
                          <input
                            type="datetime-local"
                            label="Received Date"
                            id="received_date"
                            {...field}
                            onChange={(e: any) => {
                              form.setFieldValue(
                                "received_date",
                                e.target.value
                              );
                            }}
                            disabled={disableSubmit}
                          />
                        </CustomInput>
                      )}
                    </Field>
                    <CustomInput label="Received Date Timezone">
                      {adminCase?.received_date_timezone || "UTC"}
                    </CustomInput>
                  </div>
                </>
              )}
            </fieldset>
            <fieldset className="panelContainer__section">
              <legend className="panelContainer__section--title">
                Specimen Information
              </legend>

              {adminCase.source === CaseAdmin.source.PW ? (
                <>
                  <Field name="type">
                    {({ field }: any) => (
                      <SelectInput
                        label="Specimen Type"
                        options={Object.values(SpecimenAdmin.type).map((a) => ({
                          label: a,
                          value: a,
                        }))}
                        {...field}
                        disabled={disableSubmit}
                      />
                    )}
                  </Field>
                  <Field name="site">
                    {({ field }: any) => (
                      <TextInput
                        label="Site"
                        id="site"
                        {...field}
                        disabled={disableSubmit}
                        spellCheck
                      />
                    )}
                  </Field>
                  <Field name="procedure">
                    {({ field }: any) => (
                      <SelectInput
                        label="Procedure"
                        options={Object.values(SpecimenAdmin.procedure).map(
                          (a) => ({
                            label: procedureEnumToLabel(a),
                            value: a,
                          })
                        )}
                        {...field}
                        disabled={disableSubmit}
                      />
                    )}
                  </Field>
                </>
              ) : (
                <Field name="source">
                  {({ field }: any) => (
                    <TextInput
                      label="Source"
                      id="source"
                      {...field}
                      disabled={disableSubmit}
                      spellCheck
                    />
                  )}
                </Field>
              )}

              <Field name="clinical_history">
                {({ field }: any) => (
                  <TextareaInput
                    label="Clinical History"
                    id="clinical_history"
                    rows={4}
                    {...field}
                    disabled={disableSubmit}
                  />
                )}
              </Field>
              <Field name="gross_description">
                {({ field }: any) => (
                  <TextareaInput
                    label="Gross Description"
                    id="gross_description"
                    rows={4}
                    {...field}
                    disabled={disableSubmit}
                  />
                )}
              </Field>
            </fieldset>
            {adminCase.source === CaseAdmin.source.PW && (
              <SpecimenFormProtocols
                specimen={values}
                setFieldValue={setFieldValue}
              />
            )}
          </div>
          <footer>
            <div className="panelContainer__inputGroup">
              <PathologyWatchButton onClick={onClose}>
                Cancel
              </PathologyWatchButton>
              <PathologyWatchButton
                color="red"
                onClick={() => setDelSpecimen(true)}
                disabled={!canDeleteSpecimen || disableSubmit}
              >
                Delete
              </PathologyWatchButton>
            </div>
            <div className="panelContainer__inputGroup">
              <PathologyWatchButton
                onClick={() => {
                  resetForm();
                }}
                disabled={!dirty}
              >
                Discard Changes
              </PathologyWatchButton>
              <PathologyWatchButton
                type="submit"
                color="green"
                disabled={!dirty || disableSubmit}
              >
                Save
              </PathologyWatchButton>
            </div>
          </footer>
        </Form>
      )}
    </Formik>
  );
};

export default SpecimenForm;
